import type { TakeoverDeadlineKind, TakeoverState } from '@/services/conversationService';
import { parseTime } from './conversationFormat';
import { takeoverStateOf } from './liveConversation';

/**
 * View model of the handoff status bar above a WhatsApp chat (livecenter
 * contract §4), derived from the server's takeover + the server clock. All
 * countdowns come from `deadline_at`; nothing here guesses a timing.
 */

/** Ring scale when the server sent no usable anchor (the salon default wait/idle time). */
export const DEFAULT_DEADLINE_WINDOW_MS = 5 * 60_000;

interface Common {
  reason: string | null;
  summary: string | null;
}

export type HandoffView =
  | { kind: 'agent' }
  | (Common & {
      kind: 'requested';
      /** Since the customer asked for a person. */
      waitingMs: number | null;
      /** Until the agent steps back in; ≤ 0 = any moment now. */
      remainingMs: number | null;
      /** Share of the deadline window still left (1 → 0) for the ring. */
      ring: number | null;
    })
  | (Common & {
      kind: 'staff';
      byMe: boolean;
      takenBy: string | null;
      deadlineKind: TakeoverDeadlineKind | null;
      remainingMs: number | null;
      ring: number | null;
      /** Customer wrote and waits for the staff member's answer. */
      customerWaitingMs: number | null;
    })
  | (Common & {
      kind: 'callback';
      sinceMs: number | null;
      /** Left of the 24 h reply window; null when unknown. */
      windowLeftMs: number | null;
      /** Staff can answer in the chat: the window is open AND the number is not blocked. */
      windowOpen: boolean;
      /** The contact's WhatsApp number is blocked at Meta: nothing we write arrives, call instead. */
      channelBlocked: boolean;
    });

export interface HandoffInput {
  takeover: TakeoverState | null | undefined;
  now: number;
  /** Logged-in account, to say "Du" for one's own takeover. */
  me: string | null;
  replyWindowUntil: string | null | undefined;
  /** Server says the reply window is open (computed over all sessions). */
  canReply: boolean;
  /** The contact's WhatsApp number is blocked at Meta. */
  channelBlocked?: boolean;
}

function elapsed(since: string | null | undefined, now: number): number | null {
  const t = parseTime(since);
  return t === null ? null : Math.max(0, now - t);
}

function latest(...values: Array<string | null | undefined>): number | null {
  const times = values.map(parseTime).filter((t): t is number => t !== null);
  return times.length ? Math.max(...times) : null;
}

/** When the running deadline window began, per deadline kind. */
function deadlineAnchor(takeover: TakeoverState): number | null {
  switch (takeover.deadline_kind) {
    case 'wait':
      return latest(takeover.requested_at ?? takeover.started_at);
    case 'customer_waiting':
      return latest(takeover.customer_waiting_since);
    case 'idle':
      return latest(takeover.last_staff_at, takeover.last_customer_at, takeover.started_at);
    default:
      return null;
  }
}

function countdown(takeover: TakeoverState, now: number): { remainingMs: number | null; ring: number | null } {
  const deadline = parseTime(takeover.deadline_at);
  if (deadline === null) return { remainingMs: null, ring: null };
  const remainingMs = deadline - now;
  const anchor = deadlineAnchor(takeover);
  const window = anchor !== null && deadline - anchor > 0
    ? deadline - anchor
    : Math.max(DEFAULT_DEADLINE_WINDOW_MS, remainingMs);
  const ring = Math.min(1, Math.max(0, remainingMs / window));
  return { remainingMs, ring };
}

function sameAccount(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function deriveHandoffView({
  takeover,
  now,
  me,
  replyWindowUntil,
  canReply,
  channelBlocked = false,
}: HandoffInput): HandoffView {
  const state = takeoverStateOf(takeover);
  if (!takeover || !state) return { kind: 'agent' };
  const common: Common = {
    reason: takeover.request_reason?.trim() || null,
    summary: takeover.request_summary?.trim() || null,
  };

  if (state === 'requested') {
    return {
      ...common,
      kind: 'requested',
      waitingMs: elapsed(takeover.requested_at ?? takeover.started_at, now),
      ...countdown(takeover, now),
    };
  }

  if (state === 'staff') {
    return {
      ...common,
      kind: 'staff',
      byMe: sameAccount(takeover.taken_by, me),
      takenBy: takeover.taken_by,
      deadlineKind: takeover.deadline_kind ?? null,
      customerWaitingMs: elapsed(takeover.customer_waiting_since, now),
      ...countdown(takeover, now),
    };
  }

  const until = parseTime(replyWindowUntil);
  const windowLeftMs = until === null ? null : until - now;
  return {
    ...common,
    kind: 'callback',
    sinceMs: elapsed(takeover.callback_at ?? takeover.started_at, now),
    windowLeftMs,
    windowOpen: canReply && !channelBlocked && (windowLeftMs === null || windowLeftMs > 0),
    channelBlocked,
  };
}
